import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Trophy, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { PageShell } from "@/components/PageShell";

export default function Login() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = mode === "login"
        ? await login(form.email, form.password)
        : await register({ name: form.name, email: form.email, password: form.password, phone: form.phone });
      toast.success(`Welcome${user.name ? `, ${user.name}` : ""}!`);
      nav(user.role === "admin" ? "/admin" : "/");
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Something went wrong");
    } finally { setBusy(false); }
  };

  return (
    <PageShell>
      <div className="min-h-[80vh] grid lg:grid-cols-2">
        <div className="hidden lg:block relative sky-gradient">
          <div className="absolute inset-0 grid place-items-center p-16 text-white">
            <div>
              <Trophy size={40} className="mb-6" />
              <h2 className="font-display text-4xl leading-tight">The Darren Power Memorial Golf Classic</h2>
              <p className="mt-4 text-white/90 text-lg max-w-sm">Sign in to manage the event, enter scores, and follow the leaderboard.</p>
            </div>
          </div>
        </div>

        <div className="grid place-items-center px-6 py-16">
          <div className="w-full max-w-sm">
            <div className="flex items-center gap-2 text-slate-400 text-sm"><Lock size={14} /> Secure sign in</div>
            <h1 className="mt-3 font-display text-3xl text-slate-900">{mode === "login" ? "Welcome back" : "Create account"}</h1>

            <form onSubmit={submit} className="mt-8 space-y-4">
              {mode === "register" && (
                <input data-testid="auth-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-sky-500 outline-none" required />
              )}
              <input data-testid="auth-email" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-sky-500 outline-none" required />
              <input data-testid="auth-password" type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-sky-500 outline-none" required />
              {mode === "register" && (
                <input data-testid="auth-phone" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:ring-2 focus:ring-sky-500 outline-none" />
              )}
              <button type="submit" disabled={busy} data-testid="auth-submit" className="w-full py-3 rounded-full bg-sky-600 text-white font-semibold hover:-translate-y-0.5 transition-all disabled:opacity-60">
                {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
              </button>
            </form>

            <p className="mt-5 text-sm text-slate-500 text-center">
              {mode === "login" ? "New here? " : "Already have an account? "}
              <button data-testid="auth-toggle" onClick={() => setMode(mode === "login" ? "register" : "login")} className="font-semibold text-sky-700">
                {mode === "login" ? "Create an account" : "Sign in"}
              </button>
            </p>
            <p className="mt-8 text-xs text-center text-slate-400">
              Just browsing? <Link to="/" className="underline">Continue as guest</Link>
            </p>
          </div>
        </div>
      </div>
    </PageShell>
  );
}
